import {Category, Sector} from 'common'
import React from 'react'
import {sortBy} from 'lodash'
import {FirebaseInstances} from './helpers'
import {categoriesApi, sectorsApi, storageApi, subcategoriesApi} from 'lib/api'

type CategoryWithSeo = Category & {
  metaTitle?: string | null
  metaDescription?: string | null
}

const SECTOR_LIST_RETRY_DELAYS_MS = [500, 1000, 2000, 4000, 6000]

export function createCategoryService(_fb: FirebaseInstances) {
  const normalizeSubcategories = (category: any) => {
    const fromLegacyMap = category?.subcategories
    if (
      fromLegacyMap &&
      typeof fromLegacyMap === 'object' &&
      !Array.isArray(fromLegacyMap)
    ) {
      return fromLegacyMap
    }

    const fromRecords = Array.isArray(category?.subcategoryRecords)
      ? category.subcategoryRecords
      : Array.isArray(category?.subcategories)
      ? category.subcategories
      : []

    return fromRecords.reduce((acc: Record<string, any>, row: any) => {
      if (!row || typeof row !== 'object') return acc
      const nodeId = String(row.nodeId || row.id || '').trim()
      const name = String(row.name || '').trim()
      if (!nodeId || !name) return acc

      acc[nodeId] = {
        nodeId,
        name,
        imageUrl: row.imageUrl || ''
      }
      return acc
    }, {} as Record<string, any>)
  }

  const normalizeCategory = (category: any): CategoryWithSeo | null => {
    if (!category || typeof category !== 'object') return null
    const nodeId = category.nodeId || category.id || ''
    const name = category.name || ''
    if (!nodeId || !name) return null
    return {
      nodeId,
      slug: category.slug || '',
      name,
      metaTitle: category.metaTitle || '',
      metaDescription: category.metaDescription || '',
      imageUrl: category.imageUrl || '',
      subcategories: normalizeSubcategories(category),
      isPopularCategoryMap: category.isPopularCategoryMap || {}
    }
  }

  const normalizeSector = (sector: any): Sector | null => {
    if (!sector || typeof sector !== 'object') return null
    const nodeId = sector.nodeId || sector.id || ''
    const name = sector.name || ''
    if (!nodeId || !name) return null
    return {nodeId, name}
  }

  const extractSectorRows = (result: any) => {
    if (Array.isArray(result)) return result
    if (Array.isArray(result?.sectors)) return result.sectors
    if (Array.isArray(result?.data)) return result.data
    if (Array.isArray(result?.data?.sectors)) return result.data.sectors
    return []
  }

  const normalizeSectorList = (result: any) => {
    return extractSectorRows(result).map(normalizeSector).filter(Boolean)
  }

  const wait = (delayMs: number) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, delayMs)
    })

  const fetchSectorsWithRetry = async () => {
    for (
      let attempt = 0;
      attempt <= SECTOR_LIST_RETRY_DELAYS_MS.length;
      attempt += 1
    ) {
      try {
        const result = await sectorsApi.list()
        return normalizeSectorList(result) as Sector[]
      } catch (error) {
        if (attempt === SECTOR_LIST_RETRY_DELAYS_MS.length) {
          throw error
        }
        await wait(SECTOR_LIST_RETRY_DELAYS_MS[attempt])
      }
    }
    return []
  }

  const getSectors = async () => {
    const sectors = await fetchSectorsWithRetry()
    return sortBy(sectors as Sector[], ['name'])
  }

  const getCategories = async () => {
    try {
      const result = await categoriesApi.list<{data: CategoryWithSeo[]}>()
      const data =
        (result as any)?.data || (result as any)?.categories || result
      const categories = Array.isArray(data)
        ? data.map(normalizeCategory).filter(Boolean)
        : []
      return {
        data: (categories as CategoryWithSeo[]).sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      }
    } catch (error: unknown) {
      return {error}
    }
  }

  const getSingleCategory = async (nodeId: string) => {
    try {
      const result = await categoriesApi.get<{data: CategoryWithSeo}>(nodeId)
      const data = normalizeCategory((result as any)?.data || result)
      return {data: data as CategoryWithSeo}
    } catch (error: unknown) {
      return {error}
    }
  }

  const getSingleSector = async (nodeId: string) => {
    try {
      const result = await sectorsApi.get<{data: Sector}>(nodeId)
      const data = normalizeSector((result as any)?.data || result)
      return {data: data as Sector}
    } catch (error: unknown) {
      return {error}
    }
  }

  const createOrEditSector = async (
    {name, nodeId}: Sector,
    isEdit: boolean
  ) => {
    try {
      if (!nodeId || !name)
        throw new Error('Something went wrong while creating sector')
      await (isEdit
        ? sectorsApi.update(nodeId, {name, nodeId})
        : sectorsApi.create({name, nodeId}))
      return {message: `Sector ${isEdit ? 'Edited' : 'Added'} Successfully`}
    } catch (error: unknown) {
      return {error}
    }
  }

  const createOrEditCateogry = async (
    {
      name,
      nodeId,
      imageUrl,
      isPopularCategoryMap,
      slug,
      metaTitle,
      metaDescription
    }: CategoryWithSeo,
    isEdit: boolean
  ) => {
    try {
      if (!nodeId || !name)
        throw new Error('Something went wrong while creating category')
      const payload = {
        name,
        slug,
        nodeId,
        imageUrl,
        isPopularCategoryMap,
        metaTitle,
        metaDescription
      }
      await (isEdit
        ? categoriesApi.update(nodeId, payload)
        : categoriesApi.create(payload))
      return {message: `Category ${isEdit ? 'Edited' : 'Added'} Successfully`}
    } catch (error: unknown) {
      return {error}
    }
  }

  const createOrEditSubcateogry = async ({
    subcategoryId,
    categoryId,
    imageUrl,
    isEdit,
    name
  }: {
    subcategoryId: string
    categoryId: string
    imageUrl?: string
    isEdit: boolean
    name: string
  }) => {
    try {
      const payload: {
        name: string
        nodeId: string
        imageUrl?: string
      } = {name, nodeId: subcategoryId}
      if (imageUrl !== undefined) payload.imageUrl = imageUrl

      await (isEdit
        ? subcategoriesApi.update(categoryId, subcategoryId, payload)
        : subcategoriesApi.create(categoryId, payload))
      return {
        message: `Subcategory ${isEdit ? 'Edited' : 'Added'} Successfully`
      }
    } catch (error: unknown) {
      return {error}
    }
  }

  const deleteCateogry = async (nodeId: string) => {
    try {
      await categoriesApi.delete(nodeId)
      await storageApi.deleteObject(`categories/${nodeId}/image/category-image`)
      return {}
    } catch (error: unknown) {
      const err = error as any
      return {error: err?.message ? err?.message : err}
    }
  }

  const deleteSector = async (nodeId: string) => {
    try {
      await sectorsApi.delete(nodeId)
      return {}
    } catch (error: unknown) {
      const err = error as any
      return {error: err?.message ? err?.message : err}
    }
  }

  const deleteSubcateogry = async ({
    categoryId,
    subcategoryId
  }: {
    categoryId: string
    subcategoryId: string
  }) => {
    try {
      await subcategoriesApi.delete(categoryId, subcategoryId)
      await storageApi.deleteObject(
        `categories/${categoryId}/subcategories/${subcategoryId}/image/category-image`
      )
      return {}
    } catch (error: unknown) {
      return {error}
    }
  }

  const getCategoriesList = <T>({
    cancelCallback,
    setCategories,
    categoryId
  }: {
    categoryId?: string
    cancelCallback: (e: Error) => void
    setCategories: React.Dispatch<React.SetStateAction<T[]>>
  }) => {
    let isSubscribed = true
    const fetchData = async () => {
      try {
        const result = categoryId
          ? await categoriesApi.get<{data: Category}>(categoryId)
          : await categoriesApi.list<{data: Category[]}>()
        if (!isSubscribed) return
        const raw =
          (result as any)?.data || (result as any)?.categories || result
        if (categoryId) {
          const cat = normalizeCategory(raw) as Category
          const subs = cat?.subcategories
            ? Object.values(cat.subcategories)
            : []
          setCategories(subs as T[])
        } else {
          const categories = Array.isArray(raw)
            ? raw.map(normalizeCategory).filter(Boolean)
            : []
          setCategories(categories as T[])
        }
      } catch (e) {
        if (isSubscribed) {
          cancelCallback(e as Error)
        }
      }
    }
    fetchData()
    return () => {
      isSubscribed = false
    }
  }

  const getSectorsList = <T>({
    cancelCallback,
    setSectors
  }: {
    cancelCallback: (e: Error) => void
    setSectors: React.Dispatch<React.SetStateAction<T[]>>
  }) => {
    let isSubscribed = true
    const fetchData = async () => {
      try {
        const sectors = await fetchSectorsWithRetry()
        if (isSubscribed) {
          setSectors(sectors as T[])
        }
      } catch (e) {
        if (isSubscribed) {
          cancelCallback(e as Error)
        }
      }
    }
    fetchData()
    return () => {
      isSubscribed = false
    }
  }

  return {
    getSectors,
    getCategories,
    getSingleCategory,
    getSingleSector,
    createOrEditSector,
    createOrEditCateogry,
    createOrEditSubcateogry,
    deleteCateogry,
    deleteSector,
    deleteSubcateogry,
    getCategoriesList,
    getSectorsList
  }
}
